import PropTypes from "prop-types";
import React from "react";
import Select from "lib/common/controls/Select";
import QuarterYearSelect from "lib/hex/views/components/QuarterYearSelect";
import useKeyValueState from "lib/hex/views/components/useKeyValueState";
import { registerForm } from "lib/hex/views/formRegistry";

const FormView = ({onChange, state, data}) => (
    <div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Participant ID</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="particid"
                            className="participant form-control"
                            onCommitChange={(v) => onChange("particid", v)}
                            value={state.particid}
                            options={data.particid}
                            required
                        />
                    </div>
                </div>
                <h3>Quarter</h3>
                <QuarterYearSelect
                    quarter={state.quarter}
                    year={state.year}
                    min={data.daterange.from}
                    max={data.daterange.to}
                    onChange={onChange}
                    disabled={!state.particid}
                    required
                />
                <div className="ncdr-attention after-form-group">
                    Attention: The NCDR will not accept harvest files prior to Q1/2018.
                </div>
                <h3>Submission Type</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="submissionType"
                            className="submissionType form-control"
                            onCommitChange={(v) => onChange("submissionType", v)}
                            value={state.submissionType}
                            options={data.submissionType}
                            disabled={!state.particid}
                        />
                    </div>
                </div>
                <div className="ncdr-attention after-form-group">
                    Attention: Auxiliary Dataset Submissions are no longer accepted for Discharge dates after 6/30/2023. (COVID-19 dataset discontinued as of 7/1/23; Shockwave discontinued as of 2/1/23)
                </div>
            </div>
        </div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Export Options</h3>
                <div className="checkbox">
                    <label>
                        <input
                            type="checkbox"
                            className="exclude_diagnostic_cath_only"
                            onChange={(e) => onChange("excludeDiagnosticCathOnly", e.target.checked ? "true" : "false")}
                            checked={state.excludeDiagnosticCathOnly === "true"}
                        />
                        Exclude Diagnostic Cath Only Episodes
                    </label>
                </div>
                <div className="checkbox">
                    <label>
                        <input
                            type="checkbox"
                            className="exclude_ncdr_patient_identifiers"
                            onChange={(e) => onChange("excludePatientIdentifiers", e.target.checked ? "true" : "false")}
                            checked={state.excludePatientIdentifiers === "true"}
                        />
                        Exclude NCDR Patient Identifiers
                    </label>
                </div>
            </div>
        </div>
    </div>
);

const selectShape = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })
);

FormView.propTypes = {
    onChange: PropTypes.func.isRequired,
    state: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        submissionType: PropTypes.string,
        excludePatientIdentifiers: PropTypes.string,
        excludeDiagnosticCathOnly: PropTypes.string,
        particid: PropTypes.string,
    }),
    data: PropTypes.shape({
        particid: selectShape,
        quarter: PropTypes.string,
        year: PropTypes.string,
        submissionType: PropTypes.array,
    }),
};

const asString = v =>
    v == null ? "" : v.toString();

const initialStateWithDefaults = (state = {}) => ({
    quarter: asString(state.quarter),
    year: asString(state.year),
    particid: asString(state.particid),
    submissionType: asString(state.submissionType) || "",
    excludePatientIdentifiers: asString(state.excludePatientIdentifiers) || "false",
    excludeDiagnosticCathOnly: asString(state.excludeDiagnosticCathOnly) || "false",
});

const initialData = {
    particid: [],
    daterange: { from: [], to: [] },
};

const AccCathPciExportForm = ({initialState, needServerData, onReady, onNotReady}) => {
    const [ state, setState ] = React.useState(() =>
        initialStateWithDefaults(initialState));
    React.useEffect(
        () => setState(initialStateWithDefaults(initialState)),
        [ initialState ],
    );
    const [ data, setDataKey ] = useKeyValueState(initialData);

    React.useEffect(() => {
        needServerData({
            endpoint: "/participants",
            loadingMessage: "Loading Participants...",
            onData: (data) => {
                data = data.map(it => ({
                    label: `${it.particid}: ${it.hospname}`,
                    value: it.particid,
                }));
                data.unshift({ label: "", value: "" });
                setDataKey("particid", data);
            },
        });
    }, []);

    React.useEffect(() => {
        const { particid } = state;
        if (!particid) return;
        needServerData({
            endpoint: "/date-range",
            loadingMessage: "Loading Available Date Range...",
            params: {
                particid,
            },
            onData: (data) =>
                setDataKey(
                    "daterange",
                    { from: data.from, to: data.to, particid },
                ),
        });
    }, [state.particid]);

    React.useEffect(() => {
        if (state.quarter && state.year && state.particid) {
            onReady({
                ...state,
                // for backwards compatibility
                excludeDiagnosticCathOnlyEpisodes: state.excludeDiagnosticCathOnly,
            });
        } else {
            onNotReady();
        }
    }, [state]);

    const handleChange = React.useCallback(
        (key, value) => setState(s => ({ ...s, [key]: value })),
        [ setState ],
    );

    const dataWithSubmissionTypes = {...data, submissionType: [
            { value: "", label: "" },
            { value: "1", label: "Episode of Care Records" },
            { value: "2", label: "Follow-Up Records" },
            { value: "3", label: "Auxiliary Data Collection" }
    ]};

    return (
        <FormView
            state={state}
            data={dataWithSubmissionTypes}
            onChange={handleChange}
        />
    );
};

AccCathPciExportForm.propTypes = {
    initialState: PropTypes.shape({
        quarter: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        particid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        submissionType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        excludePatientIdentifiers: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        excludeDiagnosticCathOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    }),
    needServerData: PropTypes.func.isRequired,
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

AccCathPciExportForm.canRerun = params => !!(params && params.particid);

AccCathPciExportForm.apiPrefix = "cathpci";

registerForm("acc-cathpci-export", AccCathPciExportForm);


// WEBPACK FOOTER //
// ./export_forms/api-cathpci-2026.02.24-jar/hybrid-jsclient/AccCathPciExportForm.js